
// src/Components/Admin/AdminDashboard.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Users, Search, RefreshCw, Plus, Edit, Trash2, Download, 
  Eye, Filter, SortAsc, SortDesc, Mail, CheckCircle, X, 
  AlertCircle, FileSpreadsheet, Clock, Star, Crown, User,
  Calendar, Globe, Heart, GraduationCap, Plane, Home, Briefcase,
  DollarSign, Shield, Info, ChevronDown, ChevronUp, Upload,
  File as FileIcon, MapPin, Phone, Building, CreditCard, 
  HeartPulse, Scale, BookOpen, UploadCloud, Paperclip, Folder,
  FileText, ImageIcon, Video, Archive, Music, 
  FileCode, FileImage, FileVideo, FileAudio, FileArchive,
  FileCheck, FileX, FileWarning, FileMinus, FilePlus,
  EyeOff, Link2, LogOut, Menu, Layout, List, UserPlus, UserCheck,
  UserX, UserMinus, Lock, Unlock, Settings, BarChart2, PieChart,
  MessageSquare, Bell, HelpCircle, Database, Grid, Sliders,
  ArrowLeft, ArrowRight, Maximize2, Minimize2
} from 'lucide-react';
import './AdminDashboard.css';

function AdminDashboard() {
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('view'); // 'view', 'edit', 'delete', 'assign', 'addUser'
  const [formData, setFormData] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeView, setActiveView] = useState('all'); // 'all', 'under_review', 'draft', 'users', 'add_user'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [assignedApplications, setAssignedApplications] = useState([]);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    status: 'active'
  });
  const [userErrors, setUserErrors] = useState({});

  // Country list for dropdowns
  const COUNTRY_LIST = [
    "United States", "Canada", "Mexico", "Germany", "France", "Italy", "Spain",
    "United Kingdom", "China", "Japan", "Australia", "New Zealand", "Brazil",
    "Argentina", "South Africa", "Egypt", "Russia", "India", "Pakistan", "Bangladesh",
    "Turkey", "Saudi Arabia", "UAE", "Malaysia", "Singapore", "Thailand", "Indonesia",
    "Vietnam", "South Korea", "Nigeria", "Kenya", "Morocco", "Greece", "Portugal",
    "Sweden", "Norway", "Denmark", "Netherlands", "Belgium", "Switzerland"
  ];

  // Degree options for education
  const DEGREE_OPTIONS = [
    "High School", "Associate Degree", "Bachelor's Degree", "Master's Degree",
    "Doctorate", "Diploma/Certificate", "Uneducated"
  ];

  // Disease options for health
  const DISEASE_OPTIONS = [
    "Tuberculosis", "HIV", "Hepatitis B", "Hepatitis C", "Malaria", "Cancer",
    "Diabetes", "Heart Disease", "Lung Disease", "Kidney Disease", "Liver Disease",
    "Mental Health Issues", "Stroke", "Severe Asthma", "Epilepsy", "Other"
  ];

  // Case options for criminal record
  const CASE_OPTIONS = [
    "Fraud", "Drug Trafficking", "Human Trafficking", "Money Laundering", "Violent Crime",
    "Theft", "Bribery", "Terrorism", "Smuggling", "Forgery", "Cyber Crime", "Arson",
    "Tax Evasion", "Assault", "Corruption", "Other"
  ];

  // Status options
  const [statuses] = useState([
    { value: 'all', label: 'All Statuses', color: '#6B7280' },
    { value: 'draft', label: 'Draft', color: '#6B7280' },
    { value: 'submitted', label: 'Submitted', color: '#3B82F6' },
    { value: 'under_review', label: 'Under Review', color: '#715ab7' },
    { value: 'approved', label: 'Approved', color: '#10B981' },
    { value: 'rejected', label: 'Rejected', color: '#EF4444' }
  ]);

  // Sidebar navigation items
  const sidebarItems = [
    { id: 'all', label: 'All Applications', icon: Layout, count: applications.length },
    { id: 'under_review', label: 'Under Review', icon: RefreshCw, count: applications.filter(app => app.status === 'under_review').length },
    { id: 'draft', label: 'Draft Applications', icon: FileText, count: applications.filter(app => app.status === 'draft').length },
    { id: 'users', label: 'User List', icon: Users, count: users.length },
    { id: 'add_user', label: 'Add User', icon: UserPlus },
    { id: 'logout', label: 'Logout', icon: LogOut }
  ];

  // Initialize login state
  useEffect(() => {

  // Create a style element
  const style = document.createElement('style');
  style.textContent = `
    .main-header,
    .main-footer {
      display: none !important;
    }
  `;
  document.head.appendChild(style);

  return () => {
    document.head.removeChild(style);
  };
    const savedCredentials = localStorage.getItem('adminCredentials');
    if (savedCredentials) {
      const { username, password } = JSON.parse(savedCredentials);
      if (username === 'adnan' && password === 'adnan920711') {
        setIsLoggedIn(true);
        fetchApplications();
        fetchUsers();
      }
    }
  }, []);

  // Fetch applications
  const fetchApplications = async () => {
    try {
      const response = await fetch('https://api.truvz.com/api/visa-applications');
      if (!response.ok) throw new Error('Failed to fetch applications');
      const data = await response.json();
      setApplications(data.data || data);
    } catch (err) {
      setError('Error fetching applications: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await fetch('https://api.truvz.com/api/users');
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data.data || data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Filter and sort applications based on active view
  useEffect(() => {
    let filtered = [...applications];
    
    // Apply view filter
    if (activeView === 'under_review') {
      filtered = filtered.filter(app => app.status === 'under_review');
    } else if (activeView === 'draft') {
      filtered = filtered.filter(app => app.status === 'draft');
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.nationality?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter (only if not in specific view)
    if (filterStatus !== 'all' && activeView !== 'under_review' && activeView !== 'draft') {
      filtered = filtered.filter(app => app.status === filterStatus);
    }
    
    // Sort applications
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name':
          return (a.full_name || '').localeCompare(b.full_name || '');
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        default:
          return 0;
      }
    });
    
    setFilteredApplications(filtered);
    setLoading(false);
  }, [applications, searchTerm, sortBy, filterStatus, activeView]);

  // Handle login
  const handleLogin = (e) => {
    e.preventDefault();
    if (loginForm.username === 'adnan' && loginForm.password === 'adnan920711') {
      setIsLoggedIn(true);
      setLoginError('');
      localStorage.setItem('adminCredentials', JSON.stringify(loginForm));
      fetchApplications();
      fetchUsers();
    } else {
      setLoginError('Invalid username or password');
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsLoggedIn(false);
    setLoginForm({ username: '', password: '' });
    localStorage.removeItem('adminCredentials');
    setActiveView('all');
  };

  // Handle sidebar navigation
  const handleNavigation = (viewId) => {
    if (viewId === 'logout') {
      handleLogout();
    } else {
      setActiveView(viewId);
      setSearchTerm('');
      setFilterStatus('all');
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle status change
  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      const response = await fetch(`https://api.truvz.com/api/visa-applications/${applicationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) throw new Error('Failed to update status');
      fetchApplications();
      alert(`Application status updated to ${newStatus} successfully!`);
    } catch (err) {
      alert('Error updating status: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Handle delete application
  const handleDelete = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }
    try {
      const response = await fetch(`https://api.truvz.com/api/visa-applications/${applicationId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete application');
      fetchApplications();
      alert('Application deleted successfully!');
    } catch (err) {
      alert('Error deleting application: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Handle view application
  const handleView = (application) => {
    setSelectedApplication(application);
    setModalType('view');
    setIsModalOpen(true);
    setExpandedSections({});
  };

  // Handle edit application
  const handleEdit = (application) => {
    setSelectedApplication(application);
    setFormData({
      ...application,
      children_details: application.children_details || [],
      user_education_forms: application.user_education_forms || [],
      spouse_education_forms: application.spouse_education_forms || [],
      travel_info: application.travel_info || { countries_traveled: [] },
      country_details: application.country_details || {},
      living_history: application.living_history || [],
      employment_records: application.employment_records || [],
      contacts: application.contacts || [],
      attachments: application.attachments || []
    });
    setModalType('edit');
    setIsModalOpen(true);
    setExpandedSections({});
  };

  // Handle assign application to user
  const handleAssignApplication = (application) => {
    setSelectedApplication(application);
    setModalType('assign');
    setIsModalOpen(true);
    
    // Fetch assigned applications for this application
    fetchAssignedApplications(application.id);
  };

  // Fetch assigned applications for a specific application
  const fetchAssignedApplications = async (applicationId) => {
    try {
      const response = await fetch(`https://api.truvz.com/api/applications/${applicationId}/assigned-users`);
      if (!response.ok) throw new Error('Failed to fetch assigned users');
      const data = await response.json();
      setAssignedApplications(data.data || []);
    } catch (err) {
      console.error('Error fetching assigned users:', err);
    }
  };

  // Assign application to user
  const assignApplicationToUser = async (userId) => {
    if (!selectedApplication) return;
    
    try {
      const response = await fetch(`https://api.truvz.com/api/applications/${selectedApplication.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!response.ok) throw new Error('Failed to assign application');
      
      alert('Application assigned successfully!');
      fetchAssignedApplications(selectedApplication.id);
      fetchApplications(); // Refresh applications list
    } catch (err) {
      alert('Error assigning application: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Unassign application from user
  const unassignApplicationFromUser = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to unassign this application from the user?')) {
      return;
    }
    
    try {
      const response = await fetch(`https://api.truvz.com/api/application-assignments/${assignmentId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to unassign application');
      
      alert('Application unassigned successfully!');
      fetchAssignedApplications(selectedApplication.id);
      fetchApplications(); // Refresh applications list
    } catch (err) {
      alert('Error unassigning application: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      const processedData = { ...formData };
      
      // Process date fields
      const dateFields = ['date_of_birth', 'spouse_dob'];
      dateFields.forEach(field => {
        if (processedData[field] === '') processedData[field] = null;
      });
      
      // Process children details
      if (processedData.children_details) {
        processedData.children_details = processedData.children_details.map(child => ({
          ...child,
          dob: child.dob === '' ? null : child.dob
        }));
      }
      
      // Process employment records
      if (processedData.employment_records) {
        processedData.employment_records = processedData.employment_records.map(record => ({
          ...record,
          job_start_date: record.job_start_date === '' ? null : record.job_start_date,
          job_end_date: record.job_end_date === '' ? null : record.job_end_date
        }));
      }
      
      // Process education forms
      const educationForms = ['user_education_forms', 'spouse_education_forms'];
      educationForms.forEach(formType => {
        if (processedData[formType]) {
          processedData[formType] = processedData[formType].map(form => ({
            ...form,
            completionYear: form.completionYear === '' ? null : form.completionYear
          }));
        }
      });
      
      const response = await fetch(`https://api.truvz.com/api/visa-applications/${selectedApplication.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(processedData),
      });
      
      if (!response.ok) throw new Error('Failed to update application');
      
      fetchApplications();
      setIsModalOpen(false);
      alert('Application updated successfully!');
    } catch (err) {
      alert('Error updating application: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Handle export Excel
  const handleExportExcel = async (application) => {
    try {
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = `https://api.truvz.com/api/visa-applications/${application.id}/export-excel`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert('Error downloading Excel file');
    }
  };

  // Handle export all Excel
  const handleExportAllExcel = async () => {
    if (filteredApplications.length === 0) return;
    try {
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = `https://api.truvz.com/api/visa-applications/export-excel?ids=${filteredApplications.map(app => app.id).join(',')}`;
      link.target = '_blank';
      link.download = `visa-applications-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Error exporting Excel file');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: '#6B7280', icon: FileText, label: 'Draft' },
      submitted: { color: '#3B82F6', icon: Clock, label: 'Submitted' },
      under_review: { color: '#715ab7', icon: RefreshCw, label: 'Under Review' },
      approved: { color: '#10B981', icon: CheckCircle, label: 'Approved' },
      rejected: { color: '#EF4444', icon: X, label: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <span className="status-badge" style={{ backgroundColor: config.color + '20', color: config.color }}>
        <Icon size={16} />
        {config.label}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // File input ref
  const fileInputRef = useRef(null);

  // Handle document upload
  const handleDocumentUpload = async (files) => {
    if (!files || files.length === 0 || !selectedApplication) return;
    setUploadingFiles(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      
      Array.from(files).forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
      
      const response = await fetch(`https://api.truvz.com/api/visa-applications/${selectedApplication.id}`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) throw new Error('Failed to upload documents');
      const result = await response.json();
      
      setSelectedApplication(prev => ({
        ...prev,
        attachments: result.data.attachments || []
      }));
      
      setApplications(prev => prev.map(app => 
        app.id === selectedApplication.id 
          ? { ...app, attachments: result.data.attachments || [] }
          : app
      ));
      
      setFilteredApplications(prev => prev.map(app => 
        app.id === selectedApplication.id 
          ? { ...app, attachments: result.data.attachments || [] }
          : app
      ));
      
      alert(`${files.length} document(s) uploaded successfully!`);
    } catch (err) {
      alert('Error uploading documents: ' + err.message);
      console.error('Error:', err);
    } finally {
      setUploadingFiles(false);
      setUploadProgress(0);
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleDocumentUpload(files);
    }
  };

  // Download document
  const downloadDocument = async (attachmentIndex) => {
    if (!selectedApplication) return;
    try {
      const response = await fetch(`https://api.truvz.com/api/visa-applications/${selectedApplication.id}/attachments/${attachmentIndex}/download`);
      if (!response.ok) throw new Error('Failed to download document');
      
      const contentDisposition = response.headers.get('content-disposition');
      let filename = selectedApplication.attachments[attachmentIndex]?.original_name || `document-${attachmentIndex}`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          filename = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Error downloading document: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Delete document
  const deleteDocument = async (attachmentIndex) => {
    if (!selectedApplication) return;
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }
    try {
      const response = await fetch(`https://api.truvz.com/api/visa-applications/${selectedApplication.id}/attachments/${attachmentIndex}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete document');
      
      const updatedAttachments = [...selectedApplication.attachments];
      updatedAttachments.splice(attachmentIndex, 1);
      setSelectedApplication(prev => ({
        ...prev,
        attachments: updatedAttachments
      }));
      
      setApplications(prev => prev.map(app => 
        app.id === selectedApplication.id 
          ? { ...app, attachments: updatedAttachments }
          : app
      ));
      
      setFilteredApplications(prev => prev.map(app => 
        app.id === selectedApplication.id 
          ? { ...app, attachments: updatedAttachments }
          : app
      ));
      
      alert('Document deleted successfully!');
    } catch (err) {
      alert('Error deleting document: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Get file icon
  const getFileIcon = (file) => {
    if (!file) return <FileIcon size={20} />;
    const mimeType = file.mime_type || '';
    const fileName = file.original_name || '';
    const extension = fileName.split('.').pop().toLowerCase();
    if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return <ImageIcon size={20} />;
    } else if (mimeType.includes('pdf') || extension === 'pdf') {
      return <FileText size={20} />;
    } else if (mimeType.includes('video') || ['mp4', 'avi', 'mov', 'wmv'].includes(extension)) {
      return <Video size={20} />;
    } else if (mimeType.includes('audio') || ['mp3', 'wav', 'ogg'].includes(extension)) {
      return <Music size={20} />;
    } else if (mimeType.includes('zip') || ['zip', 'rar', '7z'].includes(extension)) {
      return <Archive size={20} />;
    } else if (mimeType.includes('word') || ['doc', 'docx'].includes(extension)) {
      return <FileText size={20} />;
    } else if (mimeType.includes('excel') || ['xls', 'xlsx'].includes(extension)) {
      return <FileSpreadsheet size={20} />;
    } else {
      return <FileIcon size={20} />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Form components (same as before)
  const FormInput = ({ label, type = "text", name, value, onChange, placeholder, required = false, icon: Icon }) => {
    return (
      <div className="form-group">
        <label className="form-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
        <div className="input-container">
          {Icon && <Icon size={18} className="input-icon" />}
          <input
            type={type}
            name={name}
            placeholder={placeholder}
            className="form-control"
            value={value || ""}
            onChange={onChange}
            required={required}
          />
        </div>
      </div>
    );
  };

  const FormSelect = ({ label, name, value, onChange, children, required = false, icon: Icon }) => {
    return (
      <div className="form-group">
        <label className="form-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
        <div className="input-container">
          {Icon && <Icon size={18} className="input-icon" />}
          <select
            name={name}
            className="form-select"
            value={value}
            onChange={onChange}
            required={required}
          >
            {children}
          </select>
        </div>
      </div>
    );
  };

  const FormTextarea = ({ label, name, value, onChange, placeholder, rows = 3, required = false }) => {
    return (
      <div className="form-group">
        <label className="form-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
        <textarea
          name={name}
          placeholder={placeholder}
          className="form-textarea"
          value={value || ""}
          onChange={onChange}
          rows={rows}
          required={required}
        />
      </div>
    );
  };

  const YesNoButtons = ({ label, name, value, onChange, required = false }) => {
    return (
      <div className="form-group yesno-group">
        <label className="form-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
        <div className="yesno-buttons">
          <button
            type="button"
            className={`yesno-button ${value === "yes" ? "selected yes" : ""}`}
            onClick={() => onChange(name, "yes")}
          >
            <CheckCircle size={16} /> Yes
          </button>
          <button
            type="button"
            className={`yesno-button ${value === "no" ? "selected no" : ""}`}
            onClick={() => onChange(name, "no")}
          >
            <X size={16} /> No
          </button>
        </div>
      </div>
    );
  };

  // Child Details Component for Edit Mode
  const ChildDetailsEdit = ({ index, child, onChange, onRemove }) => {
    return (
      <div className="info-card child-card">
        <div className="card-header">
          <div className="card-title-wrapper">
            <span className="card-title">Child {index + 1} Details</span>
            <button 
              className="remove-btn" 
              onClick={() => onRemove(index)}
              title="Remove Child"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="form-grid">
          <FormInput
            label="Full Name"
            name={`child_${index}_name`}
            value={child.name}
            onChange={(e) => onChange(index, "name", e.target.value)}
            placeholder="Child's Full Name"
            icon={User}
          />
          <div className="form-group">
            <label className="form-label">Gender</label>
            <div className="option-buttons-group">
              {["Male", "Female", "Other"].map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`option-button ${child.gender === option ? "selected" : ""}`}
                  onClick={() => onChange(index, "gender", option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <FormInput
            label="Date of Birth"
            type="date"
            name={`child_${index}_dob`}
            value={child.dob}
            onChange={(e) => onChange(index, "dob", e.target.value)}
            icon={Calendar}
          />
          <FormInput
            label="Nationality"
            name={`child_${index}_nationality`}
            value={child.nationality}
            onChange={(e) => onChange(index, "nationality", e.target.value)}
            placeholder="Child's Nationality"
            icon={Globe}
          />
        </div>
      </div>
    );
  };

  // Education Form Component for Edit Mode
  const EducationFormEdit = ({ index, education, onChange, onRemove, isSpouse = false }) => {
    return (
      <div className="info-card education-card">
        <div className="card-header">
          <div className="card-title-wrapper">
            <span className="card-title">{education.degree || 'Education'}</span>
            <button 
              className="remove-btn" 
              onClick={() => onRemove(index, isSpouse)}
              title="Remove Education"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="form-grid">
          <FormInput
            label="Field of Study"
            name={`edu_${index}_fieldOfStudy`}
            value={education.fieldOfStudy}
            onChange={(e) => onChange(index, "fieldOfStudy", e.target.value, isSpouse)}
            placeholder="Your major or field of study"
          />
          <FormInput
            label="Institution Attended"
            name={`edu_${index}_institution`}
            value={education.institution}
            onChange={(e) => onChange(index, "institution", e.target.value, isSpouse)}
            placeholder="Name of school or university"
            icon={Building}
          />
          <FormInput
            label="Completion Year"
            type="date"
            name={`edu_${index}_completionYear`}
            value={education.completionYear}
            onChange={(e) => onChange(index, "completionYear", e.target.value, isSpouse)}
            icon={Calendar}
          />
        </div>
      </div>
    );
  };

  // Employment Record Component for Edit Mode
  const EmploymentRecordEdit = ({ index, record, onChange, onRemove }) => {
    return (
      <div className="info-card employment-card">
        <div className="card-header">
          <div className="card-title-wrapper">
            <span className="card-title">Job {index + 1}</span>
            {index > 0 && (
              <button 
                className="remove-btn" 
                onClick={() => onRemove(index)}
                title="Remove Job"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        <div className="form-grid">
          <FormInput
            label="Employer Details"
            name={`job_${index}_employer_details`}
            value={record.employer_details}
            onChange={(e) => onChange(index, "employer_details", e.target.value)}
            placeholder="Company Name and Address"
            icon={Building}
          />
          <FormInput
            label="Job Title"
            name={`job_${index}_job_title`}
            value={record.job_title}
            onChange={(e) => onChange(index, "job_title", e.target.value)}
            placeholder="Your Position/Role"
          />
          <FormInput
            label="Salary"
            name={`job_${index}_salary`}
            value={record.salary}
            onChange={(e) => onChange(index, "salary", e.target.value)}
            placeholder="e.g., $50,000/year"
            icon={DollarSign}
          />
          <FormInput
            label="Start Date"
            type="date"
            name={`job_${index}_job_start_date`}
            value={record.job_start_date}
            onChange={(e) => onChange(index, "job_start_date", e.target.value)}
            icon={Calendar}
          />
          {!record.is_current_job && (
            <FormInput
              label="End Date"
              type="date"
              name={`job_${index}_job_end_date`}
              value={record.job_end_date}
              onChange={(e) => onChange(index, "job_end_date", e.target.value)}
              icon={Calendar}
            />
          )}
          <FormSelect
            label="Country"
            name={`job_${index}_country`}
            value={record.country}
            onChange={(e) => onChange(index, "country", e.target.value)}
            icon={Globe}
          >
            <option value="">Select Country</option>
            {COUNTRY_LIST.map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </FormSelect>
          <div className="form-group">
            <label className="form-label">Current Job</label>
            <div className="checkbox-field">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={record.is_current_job}
                  onChange={(e) => onChange(index, "is_current_job", e.target.checked)}
                />
                <span className="checkmark"></span>
                This is my current job
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Living History Item Component for Edit Mode
  const LivingHistoryItemEdit = ({ index, item, onChange, onRemove }) => {
    return (
      <div className="info-card living-card">
        <div className="card-header">
          <div className="card-title-wrapper">
            <span className="card-title">{item.country || 'Country'}</span>
            <button 
              className="remove-btn" 
              onClick={() => onRemove(index)}
              title="Remove Country"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="form-grid">
          <FormInput
            label="Start Date"
            type="date"
            name={`living_${index}_start_date`}
            value={item.start_date}
            onChange={(e) => onChange(index, "start_date", e.target.value)}
            icon={Calendar}
          />
          {!item.is_current && (
            <FormInput
              label="End Date"
              type="date"
              name={`living_${index}_end_date`}
              value={item.end_date}
              onChange={(e) => onChange(index, "end_date", e.target.value)}
              icon={Calendar}
            />
          )}
          <div className="form-group">
            <label className="form-label">Currently living here</label>
            <div className="checkbox-field">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={item.is_current}
                  onChange={(e) => onChange(index, "is_current", e.target.checked)}
                />
                <span className="checkmark"></span>
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Contact Item Component for Edit Mode
  const ContactItemEdit = ({ index, contact, onChange, onRemove }) => {
    return (
      <div className="info-card contact-card">
        <div className="card-header">
          <div className="card-title-wrapper">
            <span className="card-title">Contact {index + 1}</span>
            <button 
              className="remove-btn" 
              onClick={() => onRemove(index)}
              title="Remove Contact"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="form-grid">
          <FormInput
            label="Contact Name"
            name={`contact_${index}_name`}
            value={contact.name}
            onChange={(e) => onChange(index, "name", e.target.value)}
            placeholder="Contact Name"
            icon={User}
          />
          <FormInput
            label="Contact Number"
            name={`contact_${index}_number`}
            value={contact.number}
            onChange={(e) => onChange(index, "number", e.target.value)}
            placeholder="Contact Number"
          />
        </div>
      </div>
    );
  };

  // Add Child Handler
  const handleAddChild = () => {
    const newChild = {
      name: "",
      dob: "",
      nationality: "",
      gender: "",
    };
    setFormData(prev => ({
      ...prev,
      has_children: "yes",
      number_of_children: (prev.number_of_children || 0) + 1,
      children_details: [...(prev.children_details || []), newChild]
    }));
  };

  // Add Education Handler
  const handleAddEducation = (isSpouse = false) => {
    const newEducation = {
      degree: "",
      fieldOfStudy: "",
      institution: "",
      completionYear: "",
    };
    if (isSpouse) {
      setFormData(prev => ({
        ...prev,
        spouse_education_forms: [...(prev.spouse_education_forms || []), newEducation]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        user_education_forms: [...(prev.user_education_forms || []), newEducation]
      }));
    }
  };

  // Add Employment Record Handler
  const handleAddEmployment = () => {
    const newRecord = {
      employer_details: "",
      job_title: "",
      salary: "",
      job_start_date: "",
      job_end_date: "",
      country: "",
      is_current_job: false,
    };
    setFormData(prev => ({
      ...prev,
      employment_status: "Employed",
      employment_records: [...(prev.employment_records || []), newRecord]
    }));
  };

  // Add Living History Item Handler
  const handleAddLivingHistory = () => {
    const newItem = {
      country: "",
      start_date: "",
      end_date: "",
      is_current: false,
    };
    setFormData(prev => ({
      ...prev,
      living_history: [...(prev.living_history || []), newItem]
    }));
  };

  // Add Contact Handler
  const handleAddContact = () => {
    const newContact = {
      name: "",
      number: "",
    };
    setFormData(prev => ({
      ...prev,
      has_canadian_contacts: "yes",
      number_of_contacts: (prev.number_of_contacts || 0) + 1,
      contacts: [...(prev.contacts || []), newContact]
    }));
  };

  // Handle user form input
  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (userErrors[name]) {
      setUserErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate user form
  const validateUserForm = () => {
    const newErrors = {};
    
    if (!userFormData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!userFormData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(userFormData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (modalType === 'addUser' && !userFormData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setUserErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle add user
  const handleAddUser = async () => {
    if (!validateUserForm()) {
      return;
    }
    
    try {
      const response = await fetch('https://api.truvz.com/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userFormData),
      });
      
      if (!response.ok) throw new Error('Failed to add user');
      
      const result = await response.json();
      alert('User added successfully!');
      fetchUsers();
      setIsModalOpen(false);
      setUserFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        status: 'active'
      });
    } catch (err) {
      alert('Error adding user: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Handle edit user
  const handleEditUser = async () => {
    if (!validateUserForm()) {
      return;
    }
    
    try {
      const response = await fetch(`https://api.truvz.com/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userFormData),
      });
      
      if (!response.ok) throw new Error('Failed to update user');
      
      const result = await response.json();
      alert('User updated successfully!');
      fetchUsers();
      setIsModalOpen(false);
    } catch (err) {
      alert('Error updating user: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`https://api.truvz.com/api/users/${userId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Failed to delete user');
      
      alert('User deleted successfully!');
      fetchUsers();
    } catch (err) {
      alert('Error deleting user: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Handle view user
  const handleViewUser = (user) => {
    setSelectedUser(user);
    setUserFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      status: user.status
    });
    setModalType('editUser');
    setIsModalOpen(true);
  };

  // Handle add new user
  const handleAddNewUser = () => {
    setUserFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
      status: 'active'
    });
    setModalType('addUser');
    setIsModalOpen(true);
  };

  // Toggle sidebar collapse
  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => !prev);
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login-container">
       
        <div className="admin-login-form">
          <div className="login-header">
            <Crown size={48} className="login-icon" />
            <h2>Admin Login</h2>
            <p>Please enter your credentials to access the dashboard</p>
          </div>
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                placeholder="Enter your username"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                placeholder="Enter your password"
                required
              />
            </div>
            {loginError && <div className="login-error">{loginError}</div>}
            <button type="submit" className="login-button">
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-dashboard ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Crown size={24} />
            <h3>Visa Admin</h3>
          </div>
          <button 
            className="sidebar-toggle" 
            onClick={toggleSidebarCollapse}
            title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {sidebarCollapsed ? <Maximize2 size={20} /> : <Minimize2 size={20} />}
          </button>
        </div>
        
        <div className="sidebar-menu">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              className={`sidebar-item ${activeView === item.id ? 'active' : ''}`}
              onClick={() => handleNavigation(item.id)}
              title={item.label}
            >
              <item.icon size={20} />
              {!sidebarCollapsed && (
                <>
                  <span>{item.label}</span>
                  {item.count !== undefined && (
                    <span className="item-count">{item.count}</span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
        
        <div className="sidebar-footer">
          {!sidebarCollapsed && (
            <div className="admin-info">
              <User size={16} />
              <span>Admin User</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-main-content">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <button 
              className="mobile-menu-toggle"
              onClick={toggleSidebarCollapse}
            >
              <Menu size={24} />
            </button>
            <h1>
              {activeView === 'all' && 'All Applications'}
              {activeView === 'under_review' && 'Applications Under Review'}
              {activeView === 'draft' && 'Draft Applications'}
              {activeView === 'users' && 'User Management'}
              {activeView === 'add_user' && 'Add New User'}
            </h1>
            <p>
              {activeView === 'all' && 'Manage all visa applications from one central location'}
              {activeView === 'under_review' && 'Applications currently under review by the team'}
              {activeView === 'draft' && 'Applications saved as draft by applicants'}
              {activeView === 'users' && 'Manage user accounts and permissions'}
              {activeView === 'add_user' && 'Create a new user account'}
            </p>
          </div>
          <div className="header-right">
            {activeView !== 'add_user' && (
              <div className="stats">
                <div className="stat">
                  <Users size={20} />
                  <span>{applications.length} Total</span>
                </div>
                <div className="stat">
                  <Clock size={20} />
                  <span>{applications.filter(app => app.status === 'submitted').length} Submitted</span>
                </div>
                <div className="stat">
                  <Star size={20} />
                  <span>{applications.filter(app => app.status === 'approved').length} Approved</span>
                </div>
              </div>
            )}
            {activeView !== 'add_user' && activeView !== 'users' && (
              <button 
                onClick={handleExportAllExcel}
                className="export-all-btn"
              >
                <Download size={16} />
                Export All as Excel
              </button>
            )}
            {activeView === 'users' && (
              <button 
                onClick={handleAddNewUser}
                className="add-user-btn"
              >
                <UserPlus size={16} />
                Add User
              </button>
            )}
            <button onClick={handleLogout} className="logout-btn">
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>

        {/* Controls */}
        {activeView !== 'add_user' && (
          <div className="dashboard-controls">
            <div className="search-filter">
              <div className="search-input">
                <Search size={20} />
                <input
                  type="text"
                  placeholder={
                    activeView === 'users' 
                      ? "Search by name or email..." 
                      : "Search by name, email, or nationality..."
                  }
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
              {activeView === 'all' && (
                <select 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="filter-select"
                >
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              )}
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Sort by Name</option>
                {activeView !== 'users' && (
                  <option value="status">Sort by Status</option>
                )}
              </select>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => setError('')} className="close-btn">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <RefreshCw size={48} className="loading-spinner" />
            <p>Loading {activeView === 'users' ? 'users' : 'applications'}...</p>
          </div>
        )}

        {/* Applications Table */}
        {!loading && activeView !== 'add_user' && activeView !== 'users' && (
          <div className="applications-table">
            <div className="table-header">
              <div className="table-cell name">Applicant Name</div>
              <div className="table-cell email">Email</div>
              <div className="table-cell nationality">Nationality</div>
              <div className="table-cell status">Status</div>
              <div className="table-cell date">Created Date</div>
              <div className="table-cell actions">Actions</div>
            </div>
            <div className="table-body">
              {filteredApplications.length === 0 ? (
                <div className="no-results">
                  <Search size={48} />
                  <h3>No {activeView} found</h3>
                  <p>Try adjusting your search or filter criteria</p>
                </div>
              ) : (
                filteredApplications.map((application) => (
                  <div key={application.id} className="table-row">
                    <div className="table-cell name">
                      {application.full_name || 'Unnamed Applicant'}
                    </div>
                    <div className="table-cell email">
                      {application.email}
                    </div>
                    <div className="table-cell nationality">
                      {application.nationality || 'N/A'}
                    </div>
                    <div className="table-cell status">
                      <select 
                        value={application.status}
                        onChange={(e) => handleStatusChange(application.id, e.target.value)}
                        className="status-select"
                      >
                        {statuses.filter(s => s.value !== 'all').map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="table-cell date">
                      {formatDate(application.created_at)}
                    </div>
                    <div className="table-cell actions">
                      <button 
                        onClick={() => handleView(application)}
                        className="action-btn view-btn"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        onClick={() => handleEdit(application)}
                        className="action-btn edit-btn"
                        title="Edit Application"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => handleAssignApplication(application)}
                        className="action-btn assign-btn"
                        title="Assign to User"
                      >
                        <UserCheck size={16} />
                      </button>
                      <button 
                        onClick={() => handleExportExcel(application)}
                        className="action-btn export-btn"
                        title="Export to Excel"
                      >
                        <FileSpreadsheet size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(application.id)}
                        className="action-btn delete-btn"
                        title="Delete Application"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Users Table */}
        {!loading && activeView === 'users' && (
          <div className="users-table">
            <div className="table-header">
              <div className="table-cell name">User Name</div>
              <div className="table-cell email">Email</div>
              <div className="table-cell role">Role</div>
              <div className="table-cell status">Status</div>
              <div className="table-cell date">Created Date</div>
              <div className="table-cell actions">Actions</div>
            </div>
            <div className="table-body">
              {users.length === 0 ? (
                <div className="no-results">
                  <Users size={48} />
                  <h3>No users found</h3>
                  <p>Click "Add User" to create a new user account</p>
                </div>
              ) : (
                users.filter(user => 
                  user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  user.email?.toLowerCase().includes(searchTerm.toLowerCase())
                ).sort((a, b) => {
                  switch (sortBy) {
                    case 'newest':
                      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                    case 'oldest':
                      return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                    case 'name':
                      return (a.name || '').localeCompare(b.name || '');
                    default:
                      return 0;
                  }
                }).map((user) => (
                  <div key={user.id} className="table-row">
                    <div className="table-cell name">
                      {user.name || 'Unnamed User'}
                    </div>
                    <div className="table-cell email">
                      {user.email}
                    </div>
                    <div className="table-cell role">
                      <span className={`role-badge ${user.role}`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </div>
                    <div className="table-cell status">
                      <span className={`status-badge ${user.status}`}>
                        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                      </span>
                    </div>
                    <div className="table-cell date">
                      {formatDate(user.created_at)}
                    </div>
                    <div className="table-cell actions">
                      <button 
                        onClick={() => handleViewUser(user)}
                        className="action-btn view-btn"
                        title="Edit User"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user.id)}
                        className="action-btn delete-btn"
                        title="Delete User"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Add User Form */}
        {!loading && activeView === 'add_user' && (
          <div className="add-user-form-container">
            <div className="form-section">
              <div className="section-header">
                <UserPlus size={24} />
                <h3>Add New User</h3>
              </div>
              <div className="form-content">
                <div className="form-grid">
                  <FormInput
                    label="Full Name"
                    name="name"
                    value={userFormData.name}
                    onChange={handleUserFormChange}
                    placeholder="Enter user's full name"
                    required
                    icon={User}
                    error={userErrors.name}
                  />
                  <FormInput
                    label="Email Address"
                    type="email"
                    name="email"
                    value={userFormData.email}
                    onChange={handleUserFormChange}
                    placeholder="Enter user's email address"
                    required
                    icon={Mail}
                    error={userErrors.email}
                  />
                  <FormInput
                    label="Password"
                    type="password"
                    name="password"
                    value={userFormData.password}
                    onChange={handleUserFormChange}
                    placeholder="Enter password (min 6 characters)"
                    required
                    icon={Lock}
                    error={userErrors.password}
                  />
                  <FormSelect
                    label="User Role"
                    name="role"
                    value={userFormData.role}
                    onChange={handleUserFormChange}
                    icon={Users}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </FormSelect>
                  <FormSelect
                    label="Status"
                    name="status"
                    value={userFormData.status}
                    onChange={handleUserFormChange}
                    icon={CheckCircle}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </FormSelect>
                </div>
                <div className="form-actions">
                  <button 
                    type="button"
                    onClick={() => setActiveView('users')}
                    className="btn cancel-btn"
                  >
                    <ArrowLeft size={16} /> Cancel
                  </button>
                  <button 
                    type="button"
                    onClick={handleAddUser}
                    className="btn save-btn"
                  >
                    <UserPlus size={16} /> Add User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal for View/Edit/Delete/Assign/AddUser */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>
                  {modalType === 'view' && 'Application Details'}
                  {modalType === 'edit' && 'Edit Application'}
                  {modalType === 'assign' && 'Assign Application to User'}
                  {modalType === 'addUser' && 'Add New User'}
                  {modalType === 'editUser' && 'Edit User'}
                </h2>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="modal-close"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body">
                {/* View Application Modal */}
                {modalType === 'view' && selectedApplication && (
                  <div className="view-content">
                    {/* Personal Information Section */}
                    <div className="detail-section">
                      <div className="section-header" onClick={() => toggleSection('personal')}>
                        <div className="section-title">
                          <User size={20} />
                          <h3>Personal Information</h3>
                        </div>
                        <div className="section-toggle">
                          {expandedSections.personal ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      {expandedSections.personal && (
                        <div className="section-content">
                          <div className="detail-grid">
                            <div className="detail-item">
                              <strong>ID:</strong> {selectedApplication.id}
                            </div>
                            <div className="detail-item">
                              <strong>Full Name:</strong> {selectedApplication.full_name || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Date of Birth:</strong> {formatDate(selectedApplication.date_of_birth)}
                            </div>
                            <div className="detail-item">
                              <strong>Nationality:</strong> {selectedApplication.nationality || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Email:</strong> {selectedApplication.email}
                            </div>
                            <div className="detail-item">
                              <strong>Contact Number:</strong> {selectedApplication.contact_number || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Address:</strong> {selectedApplication.address || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Marital Status:</strong> {selectedApplication.marital_status || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Status:</strong> {getStatusBadge(selectedApplication.status)}
                            </div>
                            <div className="detail-item">
                              <strong>Created At:</strong> {formatDate(selectedApplication.created_at)}
                            </div>
                            <div className="detail-item">
                              <strong>Submitted At:</strong> {formatDate(selectedApplication.submitted_at)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Spouse Information Section */}
                    {(selectedApplication.marital_status === "Married" || selectedApplication.marital_status === "Separated") && selectedApplication.spouse_name && (
                      <div className="detail-section">
                        <div className="section-header" onClick={() => toggleSection('spouse')}>
                          <div className="section-title">
                            <Heart size={20} />
                            <h3>Spouse Information</h3>
                          </div>
                          <div className="section-toggle">
                            {expandedSections.spouse ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        </div>
                        {expandedSections.spouse && (
                          <div className="section-content">
                            <div className="detail-grid">
                              <div className="detail-item">
                                <strong>Spouse Name:</strong> {selectedApplication.spouse_name || 'N/A'}
                              </div>
                              <div className="detail-item">
                                <strong>Spouse DOB:</strong> {formatDate(selectedApplication.spouse_dob)}
                              </div>
                              <div className="detail-item">
                                <strong>Spouse Nationality:</strong> {selectedApplication.spouse_nationality || 'N/A'}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Children Information Section */}
                    {Array.isArray(selectedApplication.children_details) && selectedApplication.children_details.length > 0 && (
                      <div className="detail-section">
                        <div className="section-header" onClick={() => toggleSection('children')}>
                          <div className="section-title">
                            <Users size={20} />
                            <h3>Children Information</h3>
                          </div>
                          <div className="section-toggle">
                            {expandedSections.children ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        </div>
                        {expandedSections.children && (
                          <div className="section-content">
                            {selectedApplication.children_details.map((child, index) => (
                              <div key={index} className="child-detail card">
                                <h4>Child {index + 1}</h4>
                                <div className="detail-grid">
                                  <div className="detail-item">
                                    <strong>Name:</strong> {child.name || 'N/A'}
                                  </div>
                                  <div className="detail-item">
                                    <strong>Date of Birth:</strong> {formatDate(child.dob)}
                                  </div>
                                  <div className="detail-item">
                                    <strong>Nationality:</strong> {child.nationality || 'N/A'}
                                  </div>
                                  <div className="detail-item">
                                    <strong>Gender:</strong> {child.gender || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Education Information Section */}
                    {(Array.isArray(selectedApplication.user_education_forms) && selectedApplication.user_education_forms.length > 0) || 
                     (Array.isArray(selectedApplication.spouse_education_forms) && selectedApplication.spouse_education_forms.length > 0) && (
                      <div className="detail-section">
                        <div className="section-header" onClick={() => toggleSection('education')}>
                          <div className="section-title">
                            <GraduationCap size={20} />
                            <h3>Education Information</h3>
                          </div>
                          <div className="section-toggle">
                            {expandedSections.education ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        </div>
                        {expandedSections.education && (
                          <div className="section-content">
                            {selectedApplication.user_education_forms && selectedApplication.user_education_forms.length > 0 && (
                              <>
                                <h4>Your Education</h4>
                                {selectedApplication.user_education_forms.map((edu, index) => (
                                  <div key={index} className="education-item card">
                                    <h5>{edu.degree || 'Degree'}</h5>
                                    <div className="detail-grid">
                                      <div className="detail-item">
                                        <strong>Field of Study:</strong> {edu.fieldOfStudy || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Institution:</strong> {edu.institution || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Completion Year:</strong> {edu.completionYear || 'N/A'}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                            {selectedApplication.spouse_education_forms && selectedApplication.spouse_education_forms.length > 0 && (
                              <>
                                <h4>Spouse's Education</h4>
                                {selectedApplication.spouse_education_forms.map((edu, index) => (
                                  <div key={index} className="education-item card">
                                    <h5>{edu.degree || 'Degree'}</h5>
                                    <div className="detail-grid">
                                      <div className="detail-item">
                                        <strong>Field of Study:</strong> {edu.fieldOfStudy || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Institution:</strong> {edu.institution || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Completion Year:</strong> {edu.completionYear || 'N/A'}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Travel History Section */}
                    {selectedApplication.travel_info?.countries_traveled && Array.isArray(selectedApplication.travel_info.countries_traveled) && selectedApplication.travel_info.countries_traveled.length > 0 && (
                      <div className="detail-section">
                        <div className="section-header" onClick={() => toggleSection('travel')}>
                          <div className="section-title">
                            <Plane size={20} />
                            <h3>Travel History</h3>
                          </div>
                          <div className="section-toggle">
                            {expandedSections.travel ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        </div>
                        {expandedSections.travel && (
                          <div className="section-content">
                            {selectedApplication.travel_info.countries_traveled.map((country, index) => (
                              <div key={index} className="travel-item card">
                                <h4>{country}</h4>
                                <div className="detail-grid">
                                  <div className="detail-item">
                                    <strong>Date Visited:</strong> {formatDate(selectedApplication.country_details?.[country]?.date_visited)}
                                  </div>
                                  <div className="detail-item">
                                    <strong>Date Return:</strong> {formatDate(selectedApplication.country_details?.[country]?.date_return)}
                                  </div>
                                  <div className="detail-item">
                                    <strong>Duration:</strong> {selectedApplication.country_details?.[country]?.duration ? `${selectedApplication.country_details[country].duration} days` : 'N/A'}
                                  </div>
                                  <div className="detail-item">
                                    <strong>Purpose:</strong> {selectedApplication.country_details?.[country]?.purpose || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Living History Section */}
                    {selectedApplication.living_history && selectedApplication.living_history.length > 0 && (
                      <div className="detail-section">
                        <div className="section-header" onClick={() => toggleSection('living')}>
                          <div className="section-title">
                            <Home size={20} />
                            <h3>Living History (Past 10 Years)</h3>
                          </div>
                          <div className="section-toggle">
                            {expandedSections.living ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        </div>
                        {expandedSections.living && (
                          <div className="section-content">
                            {selectedApplication.living_history.map((item, index) => (
                              <div key={index} className="living-item card">
                                <h4>{item.country || 'Country'}</h4>
                                <div className="detail-grid">
                                  <div className="detail-item">
                                    <strong>Start Date:</strong> {formatDate(item.start_date)}
                                  </div>
                                  <div className="detail-item">
                                    <strong>End Date:</strong> {item.is_current ? 'Current' : formatDate(item.end_date)}
                                  </div>
                                  <div className="detail-item">
                                    <strong>Current Residence:</strong> {item.is_current ? 'Yes' : 'No'}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Employment History Section */}
                    {selectedApplication.employment_status && (
                      <div className="detail-section">
                        <div className="section-header" onClick={() => toggleSection('employment')}>
                          <div className="section-title">
                            <Briefcase size={20} />
                            <h3>Employment History</h3>
                          </div>
                          <div className="section-toggle">
                            {expandedSections.employment ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        </div>
                        {expandedSections.employment && (
                          <div className="section-content">
                            <div className="detail-item">
                              <strong>Employment Status:</strong> {selectedApplication.employment_status}
                            </div>
                            {selectedApplication.employment_records && selectedApplication.employment_records.length > 0 && (
                              <>
                                <h4>Employment Records</h4>
                                {selectedApplication.employment_records.map((job, index) => (
                                  <div key={index} className="employment-item card">
                                    <h5>Job {index + 1}</h5>
                                    <div className="detail-grid">
                                      <div className="detail-item">
                                        <strong>Employer Details:</strong> {job.employer_details || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Job Title:</strong> {job.job_title || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Salary:</strong> {job.salary || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Start Date:</strong> {formatDate(job.job_start_date)}
                                      </div>
                                      <div className="detail-item">
                                        <strong>End Date:</strong> {job.is_current_job ? 'Current' : formatDate(job.job_end_date)}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Country:</strong> {job.country || 'N/A'}
                                      </div>
                                      <div className="detail-item">
                                        <strong>Current Job:</strong> {job.is_current_job ? 'Yes' : 'No'}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Financial Information Section */}
                    <div className="detail-section">
                      <div className="section-header" onClick={() => toggleSection('finance')}>
                        <div className="section-title">
                          <DollarSign size={20} />
                          <h3>Financial Information</h3>
                        </div>
                        <div className="section-toggle">
                          {expandedSections.finance ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      {expandedSections.finance && (
                        <div className="section-content">
                          <div className="detail-grid">
                            <div className="detail-item">
                              <strong>Bank Balance:</strong> {formatCurrency(selectedApplication.bank_balance)}
                            </div>
                            <div className="detail-item">
                              <strong>Annual Income:</strong> {formatCurrency(selectedApplication.annual_income)}
                            </div>
                            <div className="detail-item">
                              <strong>Source of Funds:</strong> {selectedApplication.fund_source || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Other Liquid Assets:</strong> {selectedApplication.financial_status || 'N/A'}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ties to Home Country Section */}
                    <div className="detail-section">
                      <div className="section-header" onClick={() => toggleSection('ties')}>
                        <div className="section-title">
                          <Home size={20} />
                          <h3>Ties to Home Country</h3>
                        </div>
                        <div className="section-toggle">
                          {expandedSections.ties ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      {expandedSections.ties && (
                        <div className="section-content">
                          <div className="detail-grid">
                            <div className="detail-item">
                              <strong>Property Ownership/Assets:</strong> {selectedApplication.property_ownership || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Long-term Commitments:</strong> {selectedApplication.long_term_commitments || 'N/A'}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Health and Security Section */}
                    <div className="detail-section">
                      <div className="section-header" onClick={() => toggleSection('health')}>
                        <div className="section-title">
                          <Shield size={20} />
                          <h3>Health and Security</h3>
                        </div>
                        <div className="section-toggle">
                          {expandedSections.health ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      {expandedSections.health && (
                        <div className="section-content">
                          <div className="detail-grid">
                            <div className="detail-item">
                              <strong>Health Issues:</strong> {selectedApplication.has_health_issues || 'N/A'}
                            </div>
                            {selectedApplication.has_health_issues === 'yes' && (
                              <>
                                <div className="detail-item">
                                  <strong>Health Condition:</strong> {selectedApplication.disease_name === 'Other' ? selectedApplication.custom_disease : selectedApplication.disease_name || 'N/A'}
                                </div>
                              </>
                            )}
                            <div className="detail-item">
                              <strong>Criminal Record:</strong> {selectedApplication.has_criminal_record || 'N/A'}
                            </div>
                            {selectedApplication.has_criminal_record === 'yes' && (
                              <>
                                <div className="detail-item">
                                  <strong>Type of Offense:</strong> {selectedApplication.case_name === 'Other' ? selectedApplication.custom_case : selectedApplication.case_name || 'N/A'}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Additional Information Section */}
                    <div className="detail-section">
                      <div className="section-header" onClick={() => toggleSection('additional')}>
                        <div className="section-title">
                          <Info size={20} />
                          <h3>Additional Information</h3>
                        </div>
                        <div className="section-toggle">
                          {expandedSections.additional ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      {expandedSections.additional && (
                        <div className="section-content">
                          {/* Family Information */}
                          <h4>Family Information</h4>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <strong>Mother's Name:</strong> {selectedApplication.mothers_name || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Mother's Phone:</strong> {selectedApplication.mothers_phone || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Father's Name:</strong> {selectedApplication.fathers_name || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Father's Phone:</strong> {selectedApplication.fathers_phone || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Parents' Address:</strong> {selectedApplication.parents_address || 'N/A'}
                            </div>
                          </div>

                          {/* Canadian Contacts */}
                          <h4>Canadian Contacts</h4>
                          <div className="detail-item">
                            <strong>Has Canadian Contacts:</strong> {selectedApplication.has_canadian_contacts || 'N/A'}
                          </div>
                          {selectedApplication.contacts && selectedApplication.contacts.length > 0 && (
                            <>
                              <h5>Contacts</h5>
                              {selectedApplication.contacts.map((contact, index) => (
                                <div key={index} className="contact-item card">
                                  <h6>Contact {index + 1}</h6>
                                  <div className="detail-grid">
                                    <div className="detail-item">
                                      <strong>Name:</strong> {contact.name || 'N/A'}
                                    </div>
                                    <div className="detail-item">
                                      <strong>Phone Number:</strong> {contact.number || 'N/A'}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}

                          {/* Accommodation in Canada */}
                          <h4>Accommodation in Canada</h4>
                          <div className="detail-item">
                            <strong>Has Accommodation:</strong> {selectedApplication.has_accommodation || 'N/A'}
                          </div>
                          {selectedApplication.has_accommodation === 'yes' && (
                            <div className="detail-item">
                              <strong>Accommodation Address:</strong> {selectedApplication.accommodation_address || 'N/A'}
                            </div>
                          )}

                          {/* Supporting Documents Section */}
                          <h4>Supporting Documents</h4>
                          <div className="documents-section">
                            {selectedApplication && Array.isArray(selectedApplication.attachments) && selectedApplication.attachments.length > 0 ? (
                              <div className="documents-grid">
                                {selectedApplication.attachments.map((attachment, index) => (
                                  <div key={index} className="document-item card">
                                    <div className="document-header">
                                      <div className="document-icon">
                                        {getFileIcon(attachment)}
                                      </div>
                                      <div className="document-info">
                                        <div className="document-name" title={attachment.original_name}>
                                          {attachment.original_name.length > 30 
                                            ? `${attachment.original_name.substring(0, 27)}...` 
                                            : attachment.original_name}
                                        </div>
                                        <div className="document-meta">
                                          <span className="document-size">{formatFileSize(attachment.size)}</span>
                                          {attachment.mime_type && (
                                            <span className="document-type">{attachment.mime_type.split('/')[1]}</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="document-actions">
                                      <button 
                                        onClick={() => downloadDocument(index)}
                                        className="document-action-btn download-btn"
                                        title="Download Document"
                                      >
                                        <Download size={14} />
                                      </button>
                                      <button 
                                        onClick={() => deleteDocument(index)}
                                        className="document-action-btn delete-btn"
                                        title="Delete Document"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="no-documents">
                                <FileIcon size={32} />
                                <p>No documents uploaded</p>
                              </div>
                            )}
                            {/* Upload New Documents Button */}
                            <div className="upload-section">
                              <button 
                                onClick={triggerFileInput}
                                className="upload-btn"
                                disabled={uploadingFiles}
                              >
                                {uploadingFiles ? (
                                  <>
                                    <RefreshCw size={16} className="spin" />
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <UploadCloud size={16} />
                                    Upload New Documents
                                  </>
                                )}
                              </button>
                              <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                multiple
                                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                                style={{ display: 'none' }}
                              />
                            </div>
                          </div>

                          {/* Additional Comments */}
                          <h4>Additional Comments</h4>
                          <div className="comments-section">
                            <p>{selectedApplication.comments || 'No additional comments provided'}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="modal-actions">
                      <button 
                        onClick={() => handleEdit(selectedApplication)}
                        className="btn edit-btn"
                      >
                        <Edit size={16} /> Edit
                      </button>
                      <button 
                        onClick={() => handleExportExcel(selectedApplication)}
                        className="btn export-btn"
                      >
                        <FileSpreadsheet size={16} /> Export Excel
                      </button>
                      <button 
                        onClick={() => handleAssignApplication(selectedApplication)}
                        className="btn assign-btn"
                      >
                        <UserCheck size={16} /> Assign to User
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit Application Modal */}
                {modalType === 'edit' && selectedApplication && (
                  <div className="edit-content">
                    {/* Personal Information Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <User size={20} />
                        <h3>Personal Information</h3>
                      </div>
                      <div className="form-grid">
                        <FormInput
                          label="Full Name"
                          name="full_name"
                          value={formData.full_name || ''}
                          onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                          placeholder="Full Name"
                          icon={User}
                        />
                        <FormInput
                          label="Date of Birth"
                          type="date"
                          name="date_of_birth"
                          value={formData.date_of_birth || ''}
                          onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                          icon={Calendar}
                        />
                        <FormInput
                          label="Nationality"
                          name="nationality"
                          value={formData.nationality || ''}
                          onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                          placeholder="Nationality"
                          icon={Globe}
                        />
                        <FormInput
                          label="Email"
                          type="email"
                          name="email"
                          value={formData.email || ''}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="Email"
                          icon={Mail}
                          required
                        />
                        <FormInput
                          label="Contact Number"
                          name="contact_number"
                          value={formData.contact_number || ''}
                          onChange={(e) => setFormData({...formData, contact_number: e.target.value})}
                          placeholder="Contact Number"
                          icon={Phone}
                        />
                        <FormTextarea
                          label="Address"
                          name="address"
                          value={formData.address || ''}
                          onChange={(e) => setFormData({...formData, address: e.target.value})}
                          placeholder="Complete Address"
                        />
                        <FormSelect
                          label="Marital Status"
                          name="marital_status"
                          value={formData.marital_status || ''}
                          onChange={(e) => setFormData({...formData, marital_status: e.target.value})}
                          icon={Heart}
                        >
                          <option value="">Select Marital Status</option>
                          <option value="Single">Single</option>
                          <option value="Married">Married</option>
                          <option value="Separated">Separated</option>
                        </FormSelect>
                      </div>
                    </div>

                    {/* Spouse Information Section (if married) */}
                    {formData.marital_status === "Married" && (
                      <div className="form-section">
                        <div className="section-header">
                          <Heart size={20} />
                          <h3>Spouse Information</h3>
                        </div>
                        <div className="form-grid">
                          <FormInput
                            label="Spouse's Full Name"
                            name="spouse_name"
                            value={formData.spouse_name || ''}
                            onChange={(e) => setFormData({...formData, spouse_name: e.target.value})}
                            placeholder="Spouse's Full Name"
                            icon={User}
                          />
                          <FormInput
                            label="Spouse's Date of Birth"
                            type="date"
                            name="spouse_dob"
                            value={formData.spouse_dob || ''}
                            onChange={(e) => setFormData({...formData, spouse_dob: e.target.value})}
                            icon={Calendar}
                          />
                          <FormInput
                            label="Spouse's Nationality"
                            name="spouse_nationality"
                            value={formData.spouse_nationality || ''}
                            onChange={(e) => setFormData({...formData, spouse_nationality: e.target.value})}
                            placeholder="Spouse's Nationality"
                            icon={Globe}
                          />
                        </div>
                      </div>
                    )}

                    {/* Children Information Section */}
                    {(formData.marital_status === "Married" || formData.marital_status === "Separated") && (
                      <div className="form-section">
                        <div className="section-header">
                          <Users size={20} />
                          <h3>Children Information</h3>
                        </div>
                        <YesNoButtons
                          label="Do you have children?"
                          name="has_children"
                          value={formData.has_children || ''}
                          onChange={(name, value) => {
                            setFormData(prev => ({
                              ...prev,
                              has_children: value,
                              number_of_children: value === "yes" ? (prev.number_of_children || 1) : 0,
                              children_details: value === "yes" ? (prev.children_details && prev.children_details.length > 0 ? prev.children_details : [{ name: "", dob: "", nationality: "", gender: "" }]) : []
                            }));
                          }}
                        />
                        {formData.has_children === "yes" && (
                          <>
                            <FormSelect
                              label="Number of Children"
                              name="number_of_children"
                              value={formData.number_of_children || ''}
                              onChange={(e) => {
                                const num = parseInt(e.target.value, 10);
                                const currentChildren = formData.children_details || [];
                                if (num > currentChildren.length) {
                                  // Add new children
                                  const newChildren = [...currentChildren];
                                  for (let i = currentChildren.length; i < num; i++) {
                                    newChildren.push({ name: "", dob: "", nationality: "", gender: "" });
                                  }
                                  setFormData(prev => ({ ...prev, number_of_children: num, children_details: newChildren }));
                                } else if (num < currentChildren.length) {
                                  // Remove children
                                  const newChildren = currentChildren.slice(0, num);
                                  setFormData(prev => ({ ...prev, number_of_children: num, children_details: newChildren }));
                                } else {
                                  setFormData(prev => ({ ...prev, number_of_children: num }));
                                }
                              }}
                              icon={Users}
                            >
                              <option value="">Select Number</option>
                              {[...Array(11).keys()].slice(1).map((i) => (
                                <option key={i} value={i}>{i}</option>
                              ))}
                            </FormSelect>
                            {formData.children_details && formData.children_details.map((child, index) => (
                              <ChildDetailsEdit
                                key={index}
                                index={index}
                                child={child}
                                onChange={(idx, field, value) => {
                                  const updatedChildren = [...formData.children_details];
                                  updatedChildren[idx] = { ...updatedChildren[idx], [field]: value };
                                  setFormData(prev => ({ ...prev, children_details: updatedChildren }));
                                }}
                                onRemove={(idx) => {
                                  const updatedChildren = formData.children_details.filter((_, i) => i !== idx);
                                  setFormData(prev => ({
                                    ...prev,
                                    number_of_children: updatedChildren.length,
                                    children_details: updatedChildren
                                  }));
                                }}
                              />
                            ))}
                            {formData.children_details && formData.children_details.length < (formData.number_of_children || 0) && (
                              <button 
                                type="button" 
                                onClick={handleAddChild}
                                className="add-button"
                              >
                                <Plus size={16} /> Add Child
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Education Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <GraduationCap size={20} />
                        <h3>Education Information</h3>
                      </div>
                      {/* Your Education */}
                      <div className="education-container">
                        <h4>Your Education</h4>
                        {formData.user_education_forms && formData.user_education_forms.map((edu, index) => (
                          <EducationFormEdit
                            key={index}
                            index={index}
                            education={edu}
                            onChange={(idx, field, value) => {
                              const updatedForms = [...formData.user_education_forms];
                              updatedForms[idx] = { ...updatedForms[idx], [field]: value };
                              setFormData(prev => ({ ...prev, user_education_forms: updatedForms }));
                            }}
                            onRemove={(idx) => {
                              const updatedForms = formData.user_education_forms.filter((_, i) => i !== idx);
                              setFormData(prev => ({ ...prev, user_education_forms: updatedForms }));
                            }}
                          />
                        ))}
                        <button 
                          type="button" 
                          onClick={() => handleAddEducation(false)}
                          className="add-button"
                        >
                          <Plus size={16} /> Add Education
                        </button>
                      </div>

                      {/* Spouse's Education (if married) */}
                      {formData.marital_status === "Married" && (
                        <div className="education-container">
                          <h4>Spouse's Education</h4>
                          {formData.spouse_education_forms && formData.spouse_education_forms.map((edu, index) => (
                            <EducationFormEdit
                              key={index}
                              index={index}
                              education={edu}
                              onChange={(idx, field, value, isSpouse) => {
                                const updatedForms = [...formData.spouse_education_forms];
                                updatedForms[idx] = { ...updatedForms[idx], [field]: value };
                                setFormData(prev => ({ ...prev, spouse_education_forms: updatedForms }));
                              }}
                              onRemove={(idx) => {
                                const updatedForms = formData.spouse_education_forms.filter((_, i) => i !== idx);
                                setFormData(prev => ({ ...prev, spouse_education_forms: updatedForms }));
                              }}
                              isSpouse={true}
                            />
                          ))}
                          <button 
                            type="button" 
                            onClick={() => handleAddEducation(true)}
                            className="add-button"
                          >
                            <Plus size={16} /> Add Spouse's Education
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Travel History Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <Plane size={20} />
                        <h3>Travel History</h3>
                      </div>
                      <FormSelect
                        label="Add a country you've visited"
                        value=""
                        onChange={(e) => {
                          const selectedCountry = e.target.value;
                          if (selectedCountry && !formData.travel_info?.countries_traveled?.includes(selectedCountry)) {
                            const updatedCountries = [...(formData.travel_info?.countries_traveled || []), selectedCountry];
                            const updatedDetails = { ...formData.country_details };
                            updatedDetails[selectedCountry] = {
                              date_visited: "",
                              date_return: "",
                              purpose: "",
                              duration: "",
                            };
                            setFormData(prev => ({
                              ...prev,
                              travel_info: {
                                ...prev.travel_info,
                                countries_traveled: updatedCountries
                              },
                              country_details: updatedDetails
                            }));
                          }
                        }}
                        icon={Globe}
                      >
                        <option value="" disabled>Select Country</option>
                        {COUNTRY_LIST.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </FormSelect>
                      {formData.travel_info?.countries_traveled?.length > 0 && (
                        <div className="travel-history-container">
                          {formData.travel_info.countries_traveled.map((country, index) => (
                            <div key={index} className="info-card travel-card">
                              <div className="card-header">
                                <div className="card-title-wrapper">
                                  <span className="card-title">{country}</span>
                                </div>
                                <button 
                                  className="remove-btn" 
                                  onClick={() => {
                                    const updatedCountries = formData.travel_info.countries_traveled.filter((c, i) => i !== index);
                                    const updatedDetails = { ...formData.country_details };
                                    delete updatedDetails[country];
                                    setFormData(prev => ({
                                      ...prev,
                                      travel_info: {
                                        ...prev.travel_info,
                                        countries_traveled: updatedCountries
                                      },
                                      country_details: updatedDetails
                                    }));
                                  }}
                                  title="Remove Country"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                              <div className="form-grid">
                                <FormInput
                                  label="Date Visited"
                                  type="date"
                                  value={formData.country_details?.[country]?.date_visited || ''}
                                  onChange={(e) => {
                                    const updatedDetails = { ...formData.country_details };
                                    updatedDetails[country] = {
                                      ...updatedDetails[country],
                                      date_visited: e.target.value
                                    };
                                    // Calculate duration if both dates are set
                                    if (updatedDetails[country].date_visited && updatedDetails[country].date_return) {
                                      const start = new Date(updatedDetails[country].date_visited);
                                      const end = new Date(updatedDetails[country].date_return);
                                      if (!isNaN(start) && !isNaN(end) && end >= start) {
                                        updatedDetails[country].duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)).toString();
                                      }
                                    }
                                    setFormData(prev => ({ ...prev, country_details: updatedDetails }));
                                  }}
                                  icon={Calendar}
                                />
                                <FormInput
                                  label="Date Return"
                                  type="date"
                                  value={formData.country_details?.[country]?.date_return || ''}
                                  onChange={(e) => {
                                    const updatedDetails = { ...formData.country_details };
                                    updatedDetails[country] = {
                                      ...updatedDetails[country],
                                      date_return: e.target.value
                                    };
                                    // Calculate duration if both dates are set
                                    if (updatedDetails[country].date_visited && updatedDetails[country].date_return) {
                                      const start = new Date(updatedDetails[country].date_visited);
                                      const end = new Date(updatedDetails[country].date_return);
                                      if (!isNaN(start) && !isNaN(end) && end >= start) {
                                        updatedDetails[country].duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)).toString();
                                      }
                                    }
                                    setFormData(prev => ({ ...prev, country_details: updatedDetails }));
                                  }}
                                  icon={Calendar}
                                />
                                <FormInput
                                  label="Purpose of Visit"
                                  value={formData.country_details?.[country]?.purpose || ''}
                                  onChange={(e) => {
                                    const updatedDetails = { ...formData.country_details };
                                    updatedDetails[country] = {
                                      ...updatedDetails[country],
                                      purpose: e.target.value
                                    };
                                    setFormData(prev => ({ ...prev, country_details: updatedDetails }));
                                  }}
                                  placeholder="e.g., Tourism, Business"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Living History Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <Home size={20} />
                        <h3>Living History (Past 10 Years)</h3>
                      </div>
                      <FormSelect
                        label="Add a country you've lived in"
                        value=""
                        onChange={(e) => {
                          const selectedCountry = e.target.value;
                          if (selectedCountry && !formData.living_history?.some(item => item.country === selectedCountry)) {
                            const newItem = {
                              country: selectedCountry,
                              start_date: "",
                              end_date: "",
                              is_current: false
                            };
                            setFormData(prev => ({
                              ...prev,
                              living_history: [...(prev.living_history || []), newItem]
                            }));
                          }
                        }}
                        icon={Globe}
                      >
                        <option value="" disabled>Select Country</option>
                        {COUNTRY_LIST.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </FormSelect>
                      {formData.living_history && formData.living_history.length > 0 && (
                        <div className="living-history-container">
                          {formData.living_history.map((item, index) => (
                            <LivingHistoryItemEdit
                              key={index}
                              index={index}
                              item={item}
                              onChange={(idx, field, value) => {
                                const updatedHistory = [...formData.living_history];
                                updatedHistory[idx] = { ...updatedHistory[idx], [field]: value };
                                // If setting as current, clear end_date
                                if (field === "is_current" && value) {
                                  updatedHistory[idx].end_date = "";
                                }
                                // If setting is_current to true on this item, set others to false
                                if (field === "is_current" && value) {
                                  updatedHistory.forEach((h, i) => {
                                    if (i !== idx) h.is_current = false;
                                  });
                                }
                                setFormData(prev => ({ ...prev, living_history: updatedHistory }));
                              }}
                              onRemove={(idx) => {
                                const updatedHistory = formData.living_history.filter((_, i) => i !== idx);
                                setFormData(prev => ({ ...prev, living_history: updatedHistory }));
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Employment Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <Briefcase size={20} />
                        <h3>Employment History</h3>
                      </div>
                      <FormSelect
                        label="Current Employment Status"
                        name="employment_status"
                        value={formData.employment_status || ''}
                        onChange={(e) => setFormData({...formData, employment_status: e.target.value})}
                        icon={Briefcase}
                      >
                        <option value="" disabled>Select Employment Status</option>
                        <option value="Employed">Employed</option>
                        <option value="Unemployed">Unemployed</option>
                        <option value="Retired">Retired</option>
                        <option value="Student">Student</option>
                        <option value="Self-Employed">Self-Employed</option>
                      </FormSelect>
                      {formData.employment_status === "Employed" && (
                        <div className="employment-container">
                          {formData.employment_records && formData.employment_records.map((record, index) => (
                            <EmploymentRecordEdit
                              key={index}
                              index={index}
                              record={record}
                              onChange={(idx, field, value) => {
                                const updatedRecords = [...formData.employment_records];
                                updatedRecords[idx] = { ...updatedRecords[idx], [field]: value };
                                // If setting as current job, set others to false
                                if (field === "is_current_job" && value) {
                                  updatedRecords.forEach((r, i) => {
                                    if (i !== idx) r.is_current_job = false;
                                  });
                                }
                                setFormData(prev => ({ ...prev, employment_records: updatedRecords }));
                              }}
                              onRemove={(idx) => {
                                const updatedRecords = formData.employment_records.filter((_, i) => i !== idx);
                                setFormData(prev => ({ ...prev, employment_records: updatedRecords }));
                              }}
                            />
                          ))}
                          <button 
                            type="button" 
                            onClick={handleAddEmployment}
                            className="add-button"
                          >
                            <Plus size={16} /> Add Employment Record
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Financial Information Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <DollarSign size={20} />
                        <h3>Financial Information</h3>
                      </div>
                      <div className="form-grid">
                        <FormInput
                          label="Bank Balance"
                          name="bank_balance"
                          value={formData.bank_balance || ''}
                          onChange={(e) => setFormData({...formData, bank_balance: e.target.value})}
                          placeholder="Available Current Balance"
                          icon={CreditCard}
                        />
                        <FormInput
                          label="Annual Income"
                          name="annual_income"
                          value={formData.annual_income || ''}
                          onChange={(e) => setFormData({...formData, annual_income: e.target.value})}
                          placeholder="Annual Income"
                          icon={DollarSign}
                        />
                        <FormInput
                          label="Source of Funds"
                          name="fund_source"
                          value={formData.fund_source || ''}
                          onChange={(e) => setFormData({...formData, fund_source: e.target.value})}
                          placeholder="Employment, business, investments, etc."
                        />
                        <FormInput
                          label="Other Liquid Assets"
                          name="financial_status"
                          value={formData.financial_status || ''}
                          onChange={(e) => setFormData({...formData, financial_status: e.target.value})}
                          placeholder="Cash, investments, or other liquid assets"
                          icon={DollarSign}
                        />
                      </div>
                    </div>

                    {/* Ties to Home Country Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <Home size={20} />
                        <h3>Ties to Home Country</h3>
                      </div>
                      <div className="form-grid">
                        <FormTextarea
                          label="Property Ownership or Significant Assets"
                          name="property_ownership"
                          value={formData.property_ownership || ''}
                          onChange={(e) => setFormData({...formData, property_ownership: e.target.value})}
                          placeholder="Describe any properties, investments, or valuable assets you own in your home country"
                          rows={4}
                        />
                        <FormTextarea
                          label="Long-term Commitments or Responsibilities"
                          name="long_term_commitments"
                          value={formData.long_term_commitments || ''}
                          onChange={(e) => setFormData({...formData, long_term_commitments: e.target.value})}
                          placeholder="Describe any family responsibilities, job commitments, or community involvements"
                          rows={4}
                        />
                      </div>
                    </div>

                    {/* Health and Security Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <Shield size={20} />
                        <h3>Health and Security</h3>
                      </div>
                      <YesNoButtons
                        label="Do you have any significant health issues?"
                        name="has_health_issues"
                        value={formData.has_health_issues || ''}
                        onChange={(name, value) => setFormData({...formData, has_health_issues: value})}
                      />
                      {formData.has_health_issues === "yes" && (
                        <>
                          <FormSelect
                            label="Health Condition"
                            value={formData.disease_name || ''}
                            onChange={(e) => setFormData({...formData, disease_name: e.target.value, custom_disease: ""})}
                            icon={HeartPulse}
                          >
                            <option value="">Select Condition</option>
                            {DISEASE_OPTIONS.map((disease, index) => (
                              <option key={index} value={disease}>{disease}</option>
                            ))}
                          </FormSelect>
                          {formData.disease_name === "Other" && (
                            <FormInput
                              label="Specify Health Condition"
                              value={formData.custom_disease || ''}
                              onChange={(e) => setFormData({...formData, custom_disease: e.target.value})}
                              placeholder="Please describe your health condition"
                            />
                          )}
                        </>
                      )}
                      <YesNoButtons
                        label="Do you have a criminal record?"
                        name="has_criminal_record"
                        value={formData.has_criminal_record || ''}
                        onChange={(name, value) => setFormData({...formData, has_criminal_record: value})}
                      />
                      {formData.has_criminal_record === "yes" && (
                        <>
                          <FormSelect
                            label="Type of Offense"
                            value={formData.case_name || ''}
                            onChange={(e) => setFormData({...formData, case_name: e.target.value, custom_case: ""})}
                            icon={Scale}
                          >
                            <option value="">Select Offense</option>
                            {CASE_OPTIONS.map((caseName, index) => (
                              <option key={index} value={caseName}>{caseName}</option>
                            ))}
                          </FormSelect>
                          {formData.case_name === "Other" && (
                            <FormInput
                              label="Specify Offense"
                              value={formData.custom_case || ''}
                              onChange={(e) => setFormData({...formData, custom_case: e.target.value})}
                              placeholder="Please describe the offense"
                            />
                          )}
                        </>
                      )}
                    </div>

                    {/* Additional Information Section */}
                    <div className="form-section">
                      <div className="section-header">
                        <Info size={20} />
                        <h3>Additional Information</h3>
                      </div>
                      {/* Family Information */}
                      <h4>Family Information</h4>
                      <div className="form-grid">
                        <FormInput
                          label="Mother's Name"
                          name="mothers_name"
                          value={formData.mothers_name || ''}
                          onChange={(e) => setFormData({...formData, mothers_name: e.target.value})}
                          placeholder="Mother's Full Name"
                          icon={User}
                        />
                        <FormInput
                          label="Mother's Phone Number"
                          name="mothers_phone"
                          value={formData.mothers_phone || ''}
                          onChange={(e) => setFormData({...formData, mothers_phone: e.target.value})}
                          placeholder="Mother's Phone Number"
                        />
                        <FormInput
                          label="Father's Name"
                          name="fathers_name"
                          value={formData.fathers_name || ''}
                          onChange={(e) => setFormData({...formData, fathers_name: e.target.value})}
                          placeholder="Father's Full Name"
                          icon={User}
                        />
                        <FormInput
                          label="Father's Phone Number"
                          name="fathers_phone"
                          value={formData.fathers_phone || ''}
                          onChange={(e) => setFormData({...formData, fathers_phone: e.target.value})}
                          placeholder="Father's Phone Number"
                        />
                        <FormTextarea
                          label="Where Do Your Parents Live?"
                          name="parents_address"
                          value={formData.parents_address || ''}
                          onChange={(e) => setFormData({...formData, parents_address: e.target.value})}
                          placeholder="Parents' Address"
                          rows={3}
                          icon={Home}
                        />
                      </div>

                      {/* Canadian Contacts */}
                      <h4>Canadian Contacts</h4>
                      <YesNoButtons
                        label="Do you have contacts or references in Canada?"
                        name="has_canadian_contacts"
                        value={formData.has_canadian_contacts || ''}
                        onChange={(name, value) => {
                          setFormData(prev => ({
                            ...prev,
                            has_canadian_contacts: value,
                            contacts: value === "yes" ? (prev.contacts && prev.contacts.length > 0 ? prev.contacts : [{ name: "", number: "" }]) : []
                          }));
                        }}
                      />
                      {formData.has_canadian_contacts === "yes" && (
                        <>
                          <FormSelect
                            label="Number of Contacts"
                            value={formData.number_of_contacts || ''}
                            onChange={(e) => {
                              const num = parseInt(e.target.value, 10);
                              const currentContacts = formData.contacts || [];
                              if (num > currentContacts.length) {
                                // Add new contacts
                                const newContacts = [...currentContacts];
                                for (let i = currentContacts.length; i < num; i++) {
                                  newContacts.push({ name: "", number: "" });
                                }
                                setFormData(prev => ({ ...prev, number_of_contacts: num, contacts: newContacts }));
                              } else if (num < currentContacts.length) {
                                // Remove contacts
                                const newContacts = currentContacts.slice(0, num);
                                setFormData(prev => ({ ...prev, number_of_contacts: num, contacts: newContacts }));
                              } else {
                                setFormData(prev => ({ ...prev, number_of_contacts: num }));
                              }
                            }}
                            icon={Users}
                          >
                            <option value="">Select Number</option>
                            {[...Array(5)].map((_, index) => (
                              <option key={index} value={index + 1}>{index + 1}</option>
                            ))}
                          </FormSelect>
                          {formData.contacts && formData.contacts.map((contact, index) => (
                            <ContactItemEdit
                              key={index}
                              index={index}
                              contact={contact}
                              onChange={(idx, field, value) => {
                                const updatedContacts = [...formData.contacts];
                                updatedContacts[idx] = { ...updatedContacts[idx], [field]: value };
                                setFormData(prev => ({ ...prev, contacts: updatedContacts }));
                              }}
                              onRemove={(idx) => {
                                const updatedContacts = formData.contacts.filter((_, i) => i !== idx);
                                setFormData(prev => ({
                                  ...prev,
                                  number_of_contacts: updatedContacts.length,
                                  contacts: updatedContacts
                                }));
                              }}
                            />
                          ))}
                          {formData.contacts && formData.contacts.length < (formData.number_of_contacts || 0) && (
                            <button 
                              type="button" 
                              onClick={handleAddContact}
                              className="add-button"
                            >
                              <Plus size={16} /> Add Contact
                            </button>
                          )}
                        </>
                      )}

                      {/* Accommodation in Canada */}
                      <h4>Accommodation in Canada</h4>
                      <YesNoButtons
                        label="Do you have accommodation arranged in Canada?"
                        name="has_accommodation"
                        value={formData.has_accommodation || ''}
                        onChange={(name, value) => {
                          setFormData(prev => ({
                            ...prev,
                            has_accommodation: value,
                            accommodation_address: value === "yes" ? (prev.accommodation_address || "") : ""
                          }));
                        }}
                      />
                      {formData.has_accommodation === "yes" && (
                        <FormInput
                          label="Accommodation Address"
                          name="accommodation_address"
                          value={formData.accommodation_address || ''}
                          onChange={(e) => setFormData({...formData, accommodation_address: e.target.value})}
                          placeholder="Full address of your accommodation in Canada"
                          icon={MapPin}
                        />
                      )}

                      {/* Supporting Documents Section */}
                      <h4>Supporting Documents</h4>
                      <div className="documents-section">
                        {Array.isArray(formData.attachments) && formData.attachments.length > 0 ? (
                          <div className="documents-grid">
                            {formData.attachments.map((attachment, index) => (
                              <div key={index} className="document-item card">
                                <div className="document-header">
                                  <div className="document-icon">
                                    {getFileIcon(attachment)}
                                  </div>
                                  <div className="document-info">
                                    <div className="document-name" title={attachment.original_name}>
                                      {attachment.original_name.length > 30 
                                        ? `${attachment.original_name.substring(0, 27)}...` 
                                        : attachment.original_name}
                                    </div>
                                    <div className="document-meta">
                                      <span className="document-size">{formatFileSize(attachment.size)}</span>
                                      {attachment.mime_type && (
                                        <span className="document-type">{attachment.mime_type.split('/')[1]}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="document-actions">
                                  <button 
                                    onClick={() => downloadDocument(index)}
                                    className="document-action-btn download-btn"
                                    title="Download Document"
                                  >
                                    <Download size={14} />
                                  </button>
                                  <button 
                                    onClick={() => deleteDocument(index)}
                                    className="document-action-btn delete-btn"
                                    title="Delete Document"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-documents">
                            <FileIcon size={32} />
                            <p>No documents uploaded</p>
                          </div>
                        )}
                        {/* Upload New Documents Button */}
                        <div className="upload-section">
                          <button 
                            onClick={triggerFileInput}
                            className="upload-btn"
                            disabled={uploadingFiles}
                          >
                            {uploadingFiles ? (
                              <>
                                <RefreshCw size={16} className="spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <UploadCloud size={16} />
                                Upload New Documents
                              </>
                            )}
                          </button>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            multiple
                            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                            style={{ display: 'none' }}
                          />
                        </div>
                      </div>

                      {/* Additional Comments */}
                      <h4>Additional Comments</h4>
                      <FormTextarea
                        label="Additional Comments"
                        name="comments"
                        value={formData.comments || ''}
                        onChange={(e) => setFormData({...formData, comments: e.target.value})}
                        placeholder="Any other information you would like to include with your application"
                        rows={5}
                      />
                    </div>

                    <div className="modal-actions">
                      <button 
                        onClick={() => setIsModalOpen(false)}
                        className="btn cancel-btn"
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleSaveEdit}
                        className="btn save-btn"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}

                {/* Assign Application to User Modal */}
                {modalType === 'assign' && selectedApplication && (
                  <div className="assign-content">
                    <div className="assign-header">
                      <UserCheck size={32} />
                      <h3>Assign Application to User</h3>
                      <p>Application ID: {selectedApplication.id} - {selectedApplication.full_name}</p>
                    </div>
                    
                   <div className="assigned-users-section">
  <div className="section-header">
    <div className="header-left">
      <Users size={22} />
      <h4>Currently Assigned Users</h4>
    </div>
    <div className="header-right">
      <span className="user-count-badge">
        {assignedApplications.length} {assignedApplications.length === 1 ? 'User' : 'Users'}
      </span>
    </div>
  </div>

  {assignedApplications.length === 0 ? (
    <div className="empty-state-card">
      <div className="empty-state-content">
        <div className="empty-icon">
          <Users size={48} />
        </div>
        <h5 className="empty-title">No Users Assigned</h5>
        <p className="empty-description">
          This application hasn't been assigned to any users yet.
        </p>
      </div>
    </div>
  ) : (
    <div className="table-responsive">
      <table className="simple-table">
        <thead>
          <tr>
            <th>User Name</th>
            <th>Email Address</th>
            <th>Role</th>
            <th>Assigned On</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {assignedApplications.map((assignment) => (
            <tr key={assignment.id}>
              <td>{assignment.user.name || 'Unnamed User'}</td>
              <td>{assignment.user.email}</td>
              <td>
                <span className={`role-badge ${assignment.user.role}`}>
                  {assignment.user.role.charAt(0).toUpperCase() + assignment.user.role.slice(1)}
                </span>
              </td>
              <td>
                {new Date(assignment.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </td>
              <td>
                <button 
                  onClick={() => unassignApplicationFromUser(assignment.id)}
                  className="action-btn delete-btn"
                  title="Unassign User"
                >
                  <UserX size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</div>
                    
                    <div className="assign-new-user-section">
                      <h4>Assign to New User</h4>
                      <div className="form-group">
                        <label className="form-label">Select User</label>
                        <div className="input-container">
                          <User size={18} className="input-icon" />
                          <select
                            className="form-select"
                            onChange={(e) => {
                              const userId = e.target.value;
                              if (userId) {
                                assignApplicationToUser(userId);
                              }
                            }}
                          >
                            <option value="">Select a user to assign...</option>
                            {users.filter(user => user.status === 'active').map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.name} ({user.email}) - {user.role}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="modal-actions">
                      <button 
                        onClick={() => setIsModalOpen(false)}
                        className="btn cancel-btn"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {/* Add User Modal */}
                {modalType === 'addUser' && (
                  <div className="add-user-content">
                    <div className="form-section">
                      <div className="section-header">
                        <UserPlus size={24} />
                        <h3>Add New User</h3>
                      </div>
                      <div className="form-content">
                        <div className="form-grid">
                          <FormInput
                            label="Full Name"
                            name="name"
                            value={userFormData.name}
                            onChange={handleUserFormChange}
                            placeholder="Enter user's full name"
                            required
                            icon={User}
                            error={userErrors.name}
                          />
                          <FormInput
                            label="Email Address"
                            type="email"
                            name="email"
                            value={userFormData.email}
                            onChange={handleUserFormChange}
                            placeholder="Enter user's email address"
                            required
                            icon={Mail}
                            error={userErrors.email}
                          />
                          <FormInput
                            label="Password"
                            type="password"
                            name="password"
                            value={userFormData.password}
                            onChange={handleUserFormChange}
                            placeholder="Enter password (min 6 characters)"
                            required
                            icon={Lock}
                            error={userErrors.password}
                          />
                          <FormSelect
                            label="User Role"
                            name="role"
                            value={userFormData.role}
                            onChange={handleUserFormChange}
                            icon={Users}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </FormSelect>
                          <FormSelect
                            label="Status"
                            name="status"
                            value={userFormData.status}
                            onChange={handleUserFormChange}
                            icon={CheckCircle}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </FormSelect>
                        </div>
                        <div className="form-actions">
                          <button 
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="btn cancel-btn"
                          >
                            <X size={16} /> Cancel
                          </button>
                          <button 
                            type="button"
                            onClick={handleAddUser}
                            className="btn save-btn"
                          >
                            <UserPlus size={16} /> Add User
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit User Modal */}
                {modalType === 'editUser' && selectedUser && (
                  <div className="edit-user-content">
                    <div className="form-section">
                      <div className="section-header">
                        <User size={24} />
                        <h3>Edit User: {selectedUser.name}</h3>
                      </div>
                      <div className="form-content">
                        <div className="form-grid">
                          <FormInput
                            label="Full Name"
                            name="name"
                            value={userFormData.name}
                            onChange={handleUserFormChange}
                            placeholder="Enter user's full name"
                            required
                            icon={User}
                            error={userErrors.name}
                          />
                          <FormInput
                            label="Email Address"
                            type="email"
                            name="email"
                            value={userFormData.email}
                            onChange={handleUserFormChange}
                            placeholder="Enter user's email address"
                            required
                            icon={Mail}
                            error={userErrors.email}
                          />
                          <FormInput
                            label="Password (leave blank to keep current)"
                            type="password"
                            name="password"
                            value={userFormData.password}
                            onChange={handleUserFormChange}
                            placeholder="Enter new password (optional)"
                            icon={Lock}
                          />
                          <FormSelect
                            label="User Role"
                            name="role"
                            value={userFormData.role}
                            onChange={handleUserFormChange}
                            icon={Users}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </FormSelect>
                          <FormSelect
                            label="Status"
                            name="status"
                            value={userFormData.status}
                            onChange={handleUserFormChange}
                            icon={CheckCircle}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </FormSelect>
                        </div>
                        <div className="form-actions">
                          <button 
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="btn cancel-btn"
                          >
                            <X size={16} /> Cancel
                          </button>
                          <button 
                            type="button"
                            onClick={handleEditUser}
                            className="btn save-btn"
                          >
                            <CheckCircle size={16} /> Save Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;